<script setup lang="ts">
import ColorPicker from 'primevue/colorpicker'
import { useMapStore } from '~/stores/map'
import { LayerLegend } from '~/types/map'

const mapStore = useMapStore()
const props = defineProps<{ legend: LayerLegend }>()
const stopCount = computed(() => mapStore.heatmapSettings.legendColorSteps || 5)
const legendStepLabels = computed(() => mapStore.heatmapSettings.legendStepLabels)

const gradientStops = computed(() => {
  if (!props.legend.gradientData) return
  const min = props.legend.gradientData.min
  const max = props.legend.gradientData.max
  const stepSize = (max - min) / (stopCount.value - 1)
  const stops = []
  for (let i = min; i <= max * 1.1; i += stepSize) {
    const value = i.toPrecision(2)
    stops.push({
      value,
      color: props.legend.gradientData.gradient(i).hex(),
      label: legendStepLabels.value
        ? legendStepLabels.value[i]
        : `${value} ${props.legend.gradientData.dataSuffix}`,
    })
  }
  return stops
})
const spritespriteScaleFactor = 0.2

const spriteSize = computed(() => {
  if (!props.legend.spriteData) return 0
  return props.legend.spriteData.spriteSize * spritespriteScaleFactor
})

const spriteImageSize = computed(() => {
  if (!props.legend.spriteData) return 0
  return props.legend.spriteData.spriteImageSize * spritespriteScaleFactor
})

const sprites = computed(() => {
  if (!props.legend.spriteData) return
  return Object.values(props.legend.spriteData.sprites)
})

const colors = computed(() => {
  if (!props.legend.spriteData) return
  return Object.values(props.legend.spriteData.colors)
})
</script>
<template>
  <div v-if="legend.visible" class="legend">
    <div class="legend__label">{{ legend.name }}</div>
    <div v-if="legend.dataLegendLabel" class="legend__data-description">
      {{ legend.dataLegendLabel }}
    </div>
    <div v-if="legend.type === 'gradient'" class="gradient">
      <div class="gradient__stops">
        <div v-for="stop in gradientStops" :key="stop.value" class="gradient__stop">
          <div class="color-marker" :style="{ backgroundColor: stop.color }"></div>
          <div class="label">{{ stop.label }}</div>
        </div>
      </div>
    </div>
    <div v-if="legend.type === 'sprite'">
      <!-- <img :src="legend.spriteData?.spriteImage" /> -->
      <div class="legend-section">
        <div class="legend__sub-label">Symboler</div>
        <div v-for="sprite in sprites" :key="sprite.label" class="symbol-wrapper">
          <div
            class="sprite"
            :style="{
              backgroundImage: `url(${legend.spriteData?.spriteImage})`,
              height: spriteSize + 'px',
              width: spriteSize + 'px',
              backgroundPosition: `-${sprite.coords[0] * spriteImageSize}px -${
                sprite.coords[1] * spriteImageSize
              }px`,
              backgroundSize: `${spriteImageSize}px ${spriteImageSize}px`,
            }"
          ></div>
          <div class="label">{{ sprite.label }}</div>
        </div>
      </div>
      <div class="legend-section">
        <div class="legend__sub-label">Färger</div>
        <div v-for="color in colors" :key="color.label" class="symbol-wrapper">
          <div class="color-marker" :style="{ backgroundColor: color.color }"></div>
          <div class="label">{{ color.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.symbol-wrapper {
  display: flex;
  align-items: center;
  margin: 0.25rem 0;
  margin-left: 0.5rem;
  .label {
    margin-left: 0.5rem;
    font-size: 0.75rem;
  }
  .sprite {
    background-repeat: no-repeat;
  }
}
.legend-section {
  margin: 0.5rem 0 0.75rem;
}
.legend__label {
  font-size: 1rem;
  color: #000;
}
.legend__sub-label {
  margin-bottom: 0.5rem;

  font-size: 0.75rem;
  font-weight: 500;
  color: #000;
}
.legend__data-description {
  font-size: 0.75rem;
  color: #555;
  margin-bottom: 0.25rem;
}
.legend {
  border-top: 1px solid #eee;
  padding-top: 1rem;
  padding-bottom: 0.75rem;
}
.gradient {
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
}
.gradient__stops {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.gradient__stop {
  display: flex;
  align-items: center;
  margin: 0 6px;
  font-size: 0.75rem;
  .label {
    white-space: nowrap;
  }
}
.color-marker {
  width: 16px;
  height: 16px;
  border-radius: 8px;
  margin-right: 0.25rem;
}
</style>
